import type { ImageResizeInfo } from './utils';
import { RelativeFrom, rotateByPointo, rotateMatrixByPointMatrix } from './utils';
import { matrix, multiply, add } from 'mathjs';
import type { Matrix } from 'mathjs';

export class Vertex2d {
    readonly _data: Matrix;

    static toCss(vertex: Vertex2d, isSize = false): Partial<CSSStyleDeclaration> {
        const res = [`${vertex.x * 100}%`, `${vertex.y * 100}%`]
        return isSize ? { width: res[0], height: res[1], } : { left: res[0], top: res[1], };
    }

    static fromMatrix(matrix: Matrix): Vertex2d {
        return new Vertex2d(matrix.get([0, 0]), matrix.get([1, 0]));
    }

    constructor(x:number, y:number) {
        this._data = Object.freeze(matrix([[x], [y]]));
    }

    get x(): number {
        return this._data.get([0, 0]);
    }

    get y(): number {
        return this._data.get([1, 0]);
    }

    scale(scale: number): Vertex2d {
        const result = multiply(this._data, scale);
        return Vertex2d.fromMatrix(result);
    }

    translate(vertex: Vertex2d): Vertex2d {
        const result = add(this._data, vertex._data);
        // @ts-ignore
        return Vertex2d.fromMatrix(result);
    }

    rotateByPointOrigin(rollTimes: number, pointOrigin: Vertex2d): Vertex2d {
        const result = rotateMatrixByPointMatrix(this._data, pointOrigin._data, rollTimes, false);
        return Vertex2d.fromMatrix(result);
    }

    toPercentage(imageResizeInfo: ImageResizeInfo, relativeTarget: RelativeFrom): Vertex2d {
        if (relativeTarget === RelativeFrom.rawImage) {
            return new Vertex2d(
                this.x / imageResizeInfo.imageRotatedWidth,
                this.y / imageResizeInfo.imageRotatedHeight
            );
        }
        if (relativeTarget === RelativeFrom.canvas) {
            return new Vertex2d(
                this.x / imageResizeInfo.wrapperWidth,
                this.y / imageResizeInfo.wrapperHeight
            );
        }
        throw new Error(`Unknown relative target: ${relativeTarget}`);
    }
}

export class Box {
    readonly _data: Matrix;

    static toCss(box: Box): Partial<CSSStyleDeclaration> {
        return {
            left: `${box.x1 * 100}%`,
            top: `${box.y1 * 100}%`,
            width: `${box.width * 100}%`,
            height: `${box.height * 100}%`,
        };
    }

    static from2Cords(vertex1: Vertex2d, vertex2: Vertex2d): Box {
        return new Box(
            Math.min(vertex1.x, vertex2.x),
            Math.min(vertex1.y, vertex2.y),
            Math.max(vertex1.x, vertex2.x),
            Math.max(vertex1.y, vertex2.y)
        );
    }

    static fromDomRect(rect:DOMRect): Box {
        return new Box(
            rect.x,
            rect.y,
            rect.x + rect.width,
            rect.y + rect.height
        )
    }

    normalize(): Box {
        return new Box(
            Math.min(this.x1, this.x2),
            Math.min(this.y1, this.y2),
            Math.max(this.x1, this.x2),
            Math.max(this.y1, this.y2)
        );
    }

    static fromMatrix(m: Matrix): Box {
        return new Box(
            m.get([0, 0]),
            m.get([1, 0]),
            m.get([0, 1]),
            m.get([1, 1])
        )
    }

    constructor(
        public readonly x1: number,
        public readonly y1: number,
        public readonly x2: number,
        public readonly y2: number
    ) {
        this._data = Object.freeze(matrix([
            [x1, x2],
            [y1, y2]
        ]));
    }

    get width(): number {
        return this.x2 - this.x1;
    }

    get height(): number {
        return this.y2 - this.y1;
    }

    get center(): Vertex2d {
        return new Vertex2d(
            (this.x1 + this.x2) / 2,
            (this.y1 + this.y2) / 2
        );
    }

    get head(): Vertex2d { // label数字的中心（即头）
        const oneWord = this.height;
        return new Vertex2d(
            this.x1 + oneWord / 2,
            this.y1 + oneWord / 2
        );
    }

    get x1y1(): Vertex2d {
        return new Vertex2d(this.x1, this.y1);
    }

    scale(scale: number): Box {
        const result = multiply(this._data, scale);
        return Box.fromMatrix(result);
    }

    translate(vertex: Vertex2d): Box {
        return new Box(
            this.x1 + vertex.x,
            this.y1 + vertex.y,
            this.x2 + vertex.x,
            this.y2 + vertex.y
        )
    }

    rotateByPointOrigin(rollTimes: number, pointOrigin: Vertex2d): Box {
        let _rollTimes = rollTimes % 4;
        _rollTimes = _rollTimes < 0 ? 4 + _rollTimes : _rollTimes;
        const result = rotateByPointo(this, pointOrigin, _rollTimes, true);
        return Box.fromMatrix(result);
    }

    toPercentage(imageResizeInfo: ImageResizeInfo, relativeTarget: RelativeFrom): Box {
        if (relativeTarget === RelativeFrom.canvas) {
            return new Box(
                this.x1 / imageResizeInfo.wrapperWidth,
                this.y1 / imageResizeInfo.wrapperHeight,
                this.x2 / imageResizeInfo.wrapperWidth,
                this.y2 / imageResizeInfo.wrapperHeight
            )
        } else {
            return new Box(
                this.x1 / imageResizeInfo.imageRotatedWidth,
                this.y1 / imageResizeInfo.imageRotatedHeight,
                this.x2 / imageResizeInfo.imageRotatedWidth,
                this.y2 / imageResizeInfo.imageRotatedHeight
            )
        }
    }

    toString(): string {
        return `(${this.x1},${this.y1}), (${this.x2},${this.y2})`;
    }

    toArray(): [number,number,number,number] {
        return [this.x1, this.y1, this.x2, this.y2];
    }
}
